import React, { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'outline' | 'outline-danger' | 'glass' | 'gradient-primary' | 'gradient-secondary';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  isActive?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  isActive = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = '',
  disabled,
  ...rest
}) => {
  // Base classes
  const baseClasses = 'btn relative overflow-hidden transition-all duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background-dark transform hover:-translate-y-0.5 active:translate-y-0 shadow-md';
  
  // Get variant classes
  const getVariantClasses = (variant: ButtonProps['variant']) => {
    switch (variant) {
      case 'primary':
        return 'bg-primary hover:bg-primary-hover text-white border-transparent';
      case 'secondary':
        return 'bg-secondary hover:bg-secondary-hover text-white border-transparent';
      case 'danger':
        return 'bg-status-danger hover:bg-status-danger/80 text-white border-transparent';
      case 'success':
        return 'bg-status-success hover:bg-status-success/80 text-white border-transparent';
      case 'outline':
        return 'bg-transparent hover:bg-background-hover text-primary border border-border-dark';
      case 'outline-danger':
        return 'bg-transparent hover:bg-status-danger/10 text-status-danger border border-status-danger/50';
      case 'glass':
        return 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/20';
      case 'gradient-primary':
        return 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-transparent';
      case 'gradient-secondary':
        return 'bg-opacity-50 bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600';
      default:
        return 'bg-primary hover:bg-primary-hover text-white border-transparent';
    }
  };
  
  // Size classes
  const sizeClasses = {
    xs: 'px-2 py-1 text-xs rounded',
    sm: 'px-3 py-1.5 text-xs rounded-md',
    md: 'px-4 py-2 text-sm rounded-lg',
    lg: 'px-6 py-3 text-base rounded-xl'
  };
  
  // Width classes
  const widthClasses = fullWidth ? 'w-full' : '';
  
  // Disabled and loading classes
  const stateClasses = (disabled || isLoading) ? 'opacity-70 cursor-not-allowed transform-none hover:shadow-none' : '';
  
  return (
    <button
      className={`
        ${baseClasses}
        ${getVariantClasses(variant)}
        ${sizeClasses[size]}
        ${widthClasses}
        ${stateClasses}
        ${isActive ? 'ring-2 ring-primary/40' : ''}
        ${className}
        ${leftIcon || rightIcon ? 'flex items-center gap-2 justify-center' : ''}
      `}
      disabled={disabled || isLoading}
      {...rest}
    >
      {/* Subtle background shimmer effect */}
      <span className="absolute inset-0 overflow-hidden rounded-lg opacity-0 group-hover:opacity-10">
        <span className="absolute top-0 left-[-100%] w-[300%] h-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer"></span>
      </span>
      
      {isLoading && (
        <div className="mr-2 w-4 h-4 rounded-full border-2 border-t-transparent border-white animate-spin"></div>
      )}
      
      {!isLoading && leftIcon && <span className="btn-icon">{leftIcon}</span>}
      <span className="relative">{children}</span>
      {!isLoading && rightIcon && <span className="btn-icon">{rightIcon}</span>}
    </button>
  );
};

export default Button; 